import { useSyncExternalStore } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { History, Trash2, ArrowRight, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { RoomStore } from "@/lib/room-store";

interface RecentRoom { roomId: string; title: string; visitedAt: number; expired?: boolean; }
interface RecentRoomsProps { onJoinRoom: (roomId: string, roomTitle: string) => void; }
const STORAGE_KEY = "syncscreen:recent_rooms";
const MAX_ENTRIES = 5;
const EMPTY_LIST: RecentRoom[] = [];
let cachedRaw: string | null = null;
let cachedSnapshot: RecentRoom[] = EMPTY_LIST;
function parseRooms(raw: string): RecentRoom[] {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return EMPTY_LIST;
    const cleaned = parsed.filter((r): r is RecentRoom => !!r && typeof r === "object" && typeof (r as RecentRoom).roomId === "string" && typeof (r as RecentRoom).title === "string" && typeof (r as RecentRoom).visitedAt === "number");
    cleaned.sort((a, b) => b.visitedAt - a.visitedAt);
    return cleaned.slice(0, MAX_ENTRIES);
  } catch { return EMPTY_LIST; }
}
function readSnapshot(): RecentRoom[] {
  if (typeof window === "undefined") return EMPTY_LIST;
  try { const raw = window.localStorage.getItem(STORAGE_KEY); if (raw === cachedRaw) return cachedSnapshot; cachedRaw = raw; cachedSnapshot = raw ? parseRooms(raw) : EMPTY_LIST; return cachedSnapshot; }
  catch { cachedSnapshot = EMPTY_LIST; return cachedSnapshot; }
}
function getServerSnapshot(): RecentRoom[] { return EMPTY_LIST; }
function subscribe(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener("storage", callback);
  return () => window.removeEventListener("storage", callback);
}
// Persist the current rooms list (minus any expired entries) to localStorage
// and notify subscribers. Called when a room is detected as expired.
function persistRooms(rooms: RecentRoom[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rooms.slice(0, MAX_ENTRIES)));
  } catch { /* ignore quota errors */ }
  cachedRaw = null; // force re-read on next snapshot
  cachedSnapshot = rooms.slice(0, MAX_ENTRIES);
  window.dispatchEvent(new StorageEvent("storage", { key: STORAGE_KEY }));
}
function formatRelativeTime(visitedAt: number): string {
  const diffMs = Date.now() - visitedAt;
  if (diffMs < 0) return "just now";
  const sec = Math.floor(diffMs / 1000); if (sec < 60) return "just now";
  const min = Math.floor(sec / 60); if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60); if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24); if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7); if (wk < 5) return `${wk}w ago`;
  try { return new Date(visitedAt).toLocaleDateString(); } catch { return "earlier"; }
}

export default function RecentRooms({ onJoinRoom }: RecentRoomsProps) {
  const rooms = useSyncExternalStore(subscribe, readSnapshot, getServerSnapshot);
  function handleClear() {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch {}
    cachedRaw = null; cachedSnapshot = EMPTY_LIST;
    if (typeof window !== "undefined") window.dispatchEvent(new StorageEvent("storage", { key: STORAGE_KEY }));
  }
  // Rejoin handler — first verifies the room still exists in Supabase. If the
  // auto-cleanup CRON has expired the room, we show a toast and drop the
  // entry from the recent list instead of navigating into a dead room.
  async function handleRejoin(room: RecentRoom) {
    let exists = true;
    try { exists = await RoomStore.checkRoomExists(room.roomId); }
    catch { exists = true; /* be permissive on network error */ }
    if (!exists) {
      toast.error("Room has expired due to inactivity", {
        description: "It has been removed from your recent rooms.",
      });
      // Drop the expired entry from localStorage so it doesn't keep showing.
      const current = readSnapshot();
      persistRooms(current.filter((r) => r.roomId !== room.roomId));
      return;
    }
    onJoinRoom(room.roomId, room.title);
  }
  if (rooms.length === 0) return null;
  return (
    <div className="glass-strong rounded-xl p-5 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.06] via-transparent to-transparent pointer-events-none" />
      <div className="mb-4 flex items-center justify-between relative">
        <div className="flex items-center gap-2.5 text-sm font-semibold">
          <span className="w-8 h-8 flex items-center justify-center">
            <History className="h-4 w-4 text-primary" />
          </span>
          Recent Rooms
          <span className="glass-subtle no-border rounded-full px-2 py-0.5 text-[10px] font-mono">{rooms.length}</span>
        </div>
        <button type="button" onClick={handleClear} className="inline-flex items-center gap-1.5 glass no-border rounded-full px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
          <Trash2 className="h-3.5 w-3.5" /> Clear
        </button>
      </div>
      <div className="flex flex-col gap-2.5 relative">
        <AnimatePresence initial={false}>
          {rooms.map((room) => (
            <motion.div key={room.roomId} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.22 }} className="overflow-hidden">
              <div className="flex items-center gap-3 rounded-lg bg-secondary hover:bg-card border border-white/[0.04] p-3 pr-2 transition-all group">
                <div className="w-10 h-10 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{room.title?.trim() || "Untitled room"}</div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-mono text-primary glass-subtle px-1.5 py-0.5 rounded-lg text-[10px]">{room.roomId}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{formatRelativeTime(room.visitedAt)}</span>
                  </div>
                </div>
                <button type="button" onClick={() => handleRejoin(room)} className="inline-flex shrink-0 items-center gap-1.5 rounded-xl glass-button-primary px-4 py-2 text-xs font-semibold transition-all active:scale-95">
                  Rejoin <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
